import React, { useContext, useRef, useEffect } from 'react';
import { ModalContext } from '../providers/ModalContext';
// Import Polaris web component types
import '../../types/polaris-web-components.d';

export const Modal = () => {
  const { modal, hide } = useContext(ModalContext);
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal on outside click (skipped in chromeless mode — the content
  // renders its own overlay, e.g. an App Bridge s-modal)
  useEffect(() => {
    if (modal.chromeless) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (modal.visible && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        hide();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [modal.visible, modal.chromeless, hide]);

  // Close modal on escape key. Kept in chromeless mode too, as an idempotent
  // backstop alongside the s-modal's own Esc handling (outside-click is skipped).
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (modal.visible && event.key === 'Escape') {
        hide();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modal.visible, hide]);

  // Lock body scroll while the modal is open
  useEffect(() => {
    if (!modal.visible || modal.chromeless) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [modal.visible, modal.chromeless]);

  if (!modal.visible) return null;

  // Chromeless: the content owns its own chrome (e.g. an App Bridge s-modal).
  // Render it bare — no backdrop, box, or dismissal handlers.
  if (modal.chromeless) {
    return <>{modal.content}</>;
  }

  const handlePrimaryAction = () => {
    if (modal.primaryAction?.onAction) {
      modal.primaryAction.onAction();
    }
    if (!modal.primaryAction?.onAction) {
      hide();
    }
  };

  const handleSecondaryAction = () => {
    if (modal.secondaryAction?.onAction) {
      modal.secondaryAction.onAction();
    }
    hide();
  };

  const handleTertiaryAction = () => {
    if (modal.tertiaryAction?.onAction) {
      modal.tertiaryAction.onAction();
    }
  };

  return (
    <div
      className="tb-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <style jsx>{`
        @keyframes tb-modal-fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .tb-modal-backdrop {
          animation: tb-modal-fade-in 0.15s ease-out;
        }
      `}</style>
      <div
        ref={modalRef}
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: modal.maxWidth || '500px',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        {modal.title && (
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid #e1e3e5',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <s-heading>{modal.title}</s-heading>
            <s-button variant="tertiary" icon="x" accessibilityLabel="Close" aria-label="Close" onClick={hide} />
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
          {typeof modal.content === 'string' ? (
            <s-text>{modal.content}</s-text>
          ) : (
            modal.content
          )}
        </div>

        {/* Footer */}
        {(modal.primaryAction || modal.secondaryAction || modal.tertiaryAction) && (
          <div
            style={{
              padding: '16px',
              borderTop: '1px solid #e1e3e5',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            {modal.secondaryAction && (
              <s-button variant="secondary" onClick={handleSecondaryAction}>
                {modal.secondaryAction.content || 'Cancel'}
              </s-button>
            )}
            {modal.tertiaryAction && (
              <s-button variant="secondary" onClick={handleTertiaryAction}>
                {modal.tertiaryAction.content}
              </s-button>
            )}
            {modal.primaryAction && (
              <s-button
                variant="primary"
                tone={modal.primaryAction.destructive ? 'critical' : undefined}
                loading={modal.loading}
                onClick={handlePrimaryAction}
              >
                {modal.primaryAction.content || 'Confirm'}
              </s-button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
